// ApplicationDetail — per-application view. Shows the Markdown evaluation
// report rendered properly, plus quick links to the tailored PDF and original
// job posting. The Apply helper tab is the workspace for everything that
// happens between "evaluated" and "submitted": deep research, cover letter,
// application form questions, and recommendation letter tailoring.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useZh } from '../lib/useProduct';
import { stageLabel } from '../lib/stageLabels';
import {
  Download,
  ExternalLink,
  Telescope,
  FileText,
  MessageSquare,
  Handshake,
  Sparkles,
  Copy,
  Upload,
  Trash2,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import StageTracker from '../components/domain/StageTracker';
import Tabs from '../components/ui/Tabs';
import Progress from '../components/ui/Progress';
import Chip, { stageVariant } from '../components/ui/Chip';
import LogoSquare from '../components/ui/LogoSquare';
import { apiGet, apiPatch } from '../lib/api';
import { adaptDataResponse } from '../lib/adapt';
import { useJobRunner } from '../lib/useJobRunner';
import { cleanLLMOutput, cleanLetterOutput } from '../lib/clean-llm-output';
import { useQuestionBatches, type QuestionBatch } from '../lib/useQuestionBatches';
import type { ApplicationStage } from '../lib/types';

const STAGES: ApplicationStage[] = ['Evaluated', 'Applied', 'Responded', 'Phone screen', 'Interview', 'Offer', 'Rejected', 'Discarded'];

interface TailoredItem {
  pdf_file: string;
  pdf_url: string;
  company_slug: string | null;
  company: string;
  date: string;
  bytes: number;
  mtime: number;
}
interface TailoredResponse { total: number; items: TailoredItem[] }

interface PrepListItem {
  file: string;
  kind: 'research' | 'prep' | 'other';
  company_slug: string | null;
  date: string | null;
  url: string;
  bytes: number;
  mtime: number;
}
interface PrepListResponse { total: number; items: PrepListItem[] }

interface RecommendationItem {
  slug: string;
  name: string;
  recommender_role?: string | null;
  uploaded_at?: number;
  bytes: number;
  mtime: number;
}

function companySlugify(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function fmtAge(mtime: number): string {
  const h = (Date.now() - mtime) / 3600_000;
  if (h < 1) return `${Math.max(1, Math.floor(h * 60))} min ago`;
  if (h < 48) return `${Math.floor(h)} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

export default function ApplicationDetail() {
  const { id } = useParams();
  const zh = useZh();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  // Honour ?tab=apply in the URL so external links (Applications "Apply →"
  // button, bookmarks) can deep-link straight to the Apply helper.
  // Each Apply-helper sub-function (research / cover / questions / recommendation)
  // is a top-level tab now, so it's discoverable without nesting. Old links
  // with `?tab=apply` still resolve to the first action tab.
  type DetailTab = 'report' | 'notes' | 'research' | 'cover' | 'questions' | 'recommendation';
  const TAB_ALIASES: Record<string, DetailTab> = { apply: 'research' };
  const rawTab = searchParams.get('tab') || 'report';
  const initialTab: DetailTab = (TAB_ALIASES[rawTab] ?? (rawTab as DetailTab));
  const VALID_TABS: DetailTab[] = ['report', 'notes', 'research', 'cover', 'questions', 'recommendation'];
  const [tab, setTab] = useState<DetailTab>(VALID_TABS.includes(initialTab) ? initialTab : 'report');
  const [notes, setNotes] = useState('');

  const q = useQuery({
    queryKey: ['data'],
    queryFn: async () => adaptDataResponse(await apiGet('/api/data')),
  });

  const apps = q.data?.applications ?? [];
  const app = apps.find((a) => a.id === id || String(a.num) === id);

  // Look up any tailored PDF for this company — shown as a Download button.
  const tailoredQ = useQuery({
    queryKey: ['cv-tailored'],
    queryFn: () => apiGet<TailoredResponse>('/api/cv/tailored'),
    staleTime: 10_000,
  });
  const latestPdf = useMemo(() => {
    if (!app) return null;
    const slug = companySlugify(app.company);
    if (!slug) return null;
    const items = tailoredQ.data?.items ?? [];
    const mine = items.filter((it) => it.company_slug === slug);
    if (mine.length === 0) return null;
    return mine.sort((a, b) => b.mtime - a.mtime)[0];
  }, [app, tailoredQ.data]);

  const patchMut = useMutation({
    mutationFn: (body: { id: string; status?: string; notes?: string }) => apiPatch('/api/applications/status', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data'] }),
  });

  if (q.isLoading) return <div className="empty">{zh ? '加载中…' : 'Loading…'}</div>;
  if (!app) {
    return (
      <div className="page-enter">
        <PageHeader
          eyebrow={zh ? '未找到' : 'Not found'}
          title={zh ? '找不到这条申请。' : 'Application missing.'}
          sub={zh ? `申请列表里没有 id 为「${id}」的记录。` : `No row for id "${id}" in applications.md.`}
        />
        <button className="btn" onClick={() => nav('/apps')}>{zh ? '← 申请列表' : '← Pipeline'}</button>
      </div>
    );
  }

  const rowId = app.id || String(app.num);

  return (
    <div className="page-enter">
      <button className="btn ghost" style={{ marginBottom: 18 }} onClick={() => nav(-1)}>
        {zh ? '← 返回' : '← Back'}
      </button>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18, marginBottom: 26 }}>
        <LogoSquare name={app.company} size={64} />
        <div style={{ flex: 1 }}>
          <div className="eyebrow">{app.company}</div>
          <div className="serif" style={{ fontSize: 38, lineHeight: 1.05, letterSpacing: '-0.02em', marginTop: 2 }}>
            {app.role}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 10, fontSize: 13, color: 'var(--ink2)', flexWrap: 'wrap' }}>
            <span>{zh ? `${app.date}` : `Applied ${app.date}`}</span>
            <span style={{ color: 'var(--line)' }}>·</span>
            <Chip variant={stageVariant(app.stage)}>{stageLabel(app.stage, zh)}</Chip>
            <span style={{ color: 'var(--line)' }}>·</span>
            <span className="num">{app.score.toFixed(1)}<span style={{ fontSize: 10 }}>/5</span></span>
            {app.url && (
              <>
                <span style={{ color: 'var(--line)' }}>·</span>
                <a
                  href={app.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: 'var(--ink3)', fontSize: 12, textDecoration: 'underline', wordBreak: 'break-all' }}
                >
                  {app.url.replace(/^https?:\/\//, '').slice(0, 80)}
                </a>
              </>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <select
            className="btn"
            value={app.stage}
            onChange={(e) => patchMut.mutate({ id: rowId, status: e.target.value })}
            style={{ padding: '7px 12px' }}
          >
            {STAGES.map((s) => <option key={s} value={s}>{stageLabel(s, zh)}</option>)}
          </select>
          {app.url && (
            <a
              className="btn"
              href={app.url}
              target="_blank"
              rel="noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <ExternalLink size={13} /> {zh ? '招聘页' : 'Posting'}
            </a>
          )}
          {/* Deep research moved into the Apply helper tab as a dedicated card.
              Tailored CV / Preview kept here because they're a frequent
              "after submit / during interview prep" reference. */}
          {latestPdf ? (
            <>
              <a
                className="btn accent"
                href={latestPdf.pdf_url}
                download={latestPdf.pdf_file}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                title={`${latestPdf.pdf_file} · ${(latestPdf.bytes / 1024).toFixed(1)} KB · ${latestPdf.date}`}
              >
                <Download size={13} /> {zh ? '定制简历' : 'Tailored CV'}
              </a>
              <a
                className="btn"
                href={latestPdf.pdf_url}
                target="_blank"
                rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <ExternalLink size={13} /> {zh ? '预览简历' : 'Preview CV'}
              </a>
            </>
          ) : (
            <button
              className="btn"
              disabled
              style={{ opacity: 0.5 }}
              title={zh ? '还没有定制简历 — 去「简历定制」生成一份' : 'No tailored CV yet — tailor one in CV Writer'}
            >
              {zh ? '暂无定制简历' : 'No tailored CV'}
            </button>
          )}
        </div>
      </div>

      <StageTracker currentStage={app.stage} />

      {/* Pre-submit journey: 5-step strip showing what's done / running /
          waiting + the final Mark-as-Applied action. Sits above the tab bar
          so it's always visible regardless of which tab the user is on. */}
      <div style={{ marginTop: 18 }}>
        <ApplyJourney
          appId={rowId}
          company={app.company}
          stage={app.stage}
          onNavigate={(t) => setTab(t as DetailTab)}
          onMarkApplied={() => patchMut.mutate({ id: rowId, status: 'Applied' })}
          markingApplied={patchMut.isPending}
        />
      </div>

      <div style={{ marginTop: 18 }}>
        <Tabs<DetailTab>
          value={tab}
          onChange={setTab}
          items={[
            { key: 'report',         label: zh ? '评估报告' : 'Report' },
            { key: 'research',       label: zh ? '公司情报' : 'Intel' },
            { key: 'cover',          label: zh ? '求职信' : 'Cover letter' },
            { key: 'questions',      label: zh ? '面试问题' : 'Questions' },
            { key: 'recommendation', label: zh ? '推荐信' : 'Recommendation letter' },
            { key: 'notes',          label: zh ? '备注' : 'Notes' },
          ]}
        />

        {tab === 'report' && (
          <div className="card" style={{ padding: 22 }}>
            {app.report_file ? (
              <ReportViewer file={app.report_file} />
            ) : (
              <div className="empty">
                <span className="serif">{zh ? '这一条没有评估报告。' : 'No report for this row.'}</span>
                {zh ? '仅手动添加的条目或很旧的记录。你仍然可以编辑状态和备注。' : 'Only manually-added entries or very old rows. You can still edit status and notes.'}
              </div>
            )}
          </div>
        )}

        {tab === 'notes' && (
          <div className="card" style={{ padding: 22 }}>
            <div className="tab" style={{ marginBottom: 10 }}>{zh ? '私人备注' : 'Private notes'}</div>
            <textarea
              value={notes || app.notes || ''}
              onChange={(e) => setNotes(e.target.value)}
              rows={12}
              style={{ width: '100%', padding: '14px 16px', border: '1px solid var(--line)', borderRadius: 8, fontSize: 13.5, fontFamily: 'Inter, var(--cjk-sans), sans-serif', background: 'var(--bg)', resize: 'vertical', lineHeight: 1.6 }}
            />
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
              <button
                className="btn accent"
                disabled={patchMut.isPending}
                onClick={() => patchMut.mutate({ id: rowId, notes })}
              >
                {patchMut.isPending ? (zh ? '保存中…' : 'Saving…') : (zh ? '保存备注' : 'Save notes')}
              </button>
              {patchMut.isSuccess && <span style={{ fontSize: 12, color: 'var(--good)', alignSelf: 'center' }}>{zh ? '已保存。' : 'Saved.'}</span>}
            </div>
          </div>
        )}

        {tab === 'research'       && <DeepResearchCard   appId={rowId} company={app.company} role={app.role} />}
        {tab === 'cover'          && <CoverLetterCard    appId={rowId} company={app.company} role={app.role} />}
        {tab === 'questions'      && <QuestionsCard      appId={rowId} company={app.company} role={app.role} />}
        {tab === 'recommendation' && <RecommendationCard appId={rowId} company={app.company} role={app.role} />}
      </div>
    </div>
  );
}

// ── ApplyJourney ─────────────────────────────────────────────────────────────
// 5-step horizontal stepper: Intel → Cover letter → Questions → Recommendation
// → Submit. Each prep node reads its underlying useJobRunner / saved-artifact
// query to derive {idle | running | done | failed}. Clicking a node jumps
// to its tab. The final "Submit" node is the Mark-as-Applied CTA — gated only
// by stage (not by prep completion); we surface progress as info, not as a
// hard prerequisite. The user can always submit; the strip just shows them
// what they've prepared.

type StepKey = 'research' | 'cover' | 'questions' | 'recommendation';
type StepState = 'idle' | 'running' | 'done' | 'failed';

interface JourneyStep {
  key: StepKey | 'submit';
  label: string;
  state: StepState | 'submitted' | 'ready' | 'locked';
}

function ApplyJourney({
  appId,
  company,
  stage,
  onNavigate,
  onMarkApplied,
  markingApplied,
}: {
  appId: string;
  company: string;
  stage: ApplicationStage;
  onNavigate: (tab: StepKey) => void;
  onMarkApplied: () => void;
  markingApplied: boolean;
}) {
  const zh = useZh();
  // Each prep step reads its useJobRunner instance. Hooks with the same key
  // share localStorage + dedupe their poll via TanStack Query, so mounting
  // here in addition to the per-tab cards is cheap.
  // Reattach configs let another device's in-flight prep job show up here
  // too. ApplyJourney only has `company` (not role), so we match by company
  // alone — if a user has two same-company different-role apps both running
  // cover_letter, the strip will light up for both, which is acceptable.
  // The per-card variants below match company + role precisely.
  const slug = companySlugify(company);
  const companyLower = (company || '').toLowerCase();
  const intelRunner = useJobRunner(`research-${slug}`, {
    reattach: { cmd: 'deep_research', match: (p) => String(p.company || '').toLowerCase() === companyLower },
  });
  const coverRunner = useJobRunner(`cover-letter-${appId}`, {
    reattach: { cmd: 'cover_letter', match: (p) => String(p.company || '').toLowerCase() === companyLower },
  });
  const questionsRunner = useJobRunner(`apply-answers-${appId}`, {
    reattach: { cmd: 'apply_assist', match: (p) => String(p.company || '').toLowerCase() === companyLower },
  });
  const recRunner = useJobRunner(`recommendation-${appId}`, {
    reattach: { cmd: 'tailor_recommendation', match: (p) => String(p.company || '').toLowerCase() === companyLower },
  });

  // Intel also has a server-saved markdown file → if we find one, count Intel
  // as done even when the runner has never executed in this browser session.
  const intelListQ = useQuery({
    queryKey: ['interview-prep-list', slug],
    queryFn: () =>
      apiGet<PrepListResponse>('/api/interview-prep/list', { company_slug: slug }),
    enabled: !!slug,
    staleTime: 30_000,
  });
  const intelHasSaved = (intelListQ.data?.items ?? []).some((i) => i.kind === 'research');

  const stateFor = (
    runner: { isRunning: boolean; status: string; isActive: boolean },
    extraDone = false,
  ): StepState =>
    runner.isRunning ? 'running'
      : runner.status === 'done' || extraDone ? 'done'
      : runner.status === 'failed' ? 'failed'
      : 'idle';

  const intelState     = stateFor(intelRunner,     intelHasSaved);
  const coverState     = stateFor(coverRunner);
  const questionsState = stateFor(questionsRunner);
  const recState       = stateFor(recRunner);

  const prepStates = [intelState, coverState, questionsState, recState];
  const doneCount = prepStates.filter((s) => s === 'done').length;
  const allPrepDone = doneCount === 4;

  // Submit step:
  //   submitted  — user already flipped past Evaluated
  //   ready      — stage Evaluated AND all 4 prep done (encouraged path)
  //   locked     — stage Evaluated, prep incomplete (still clickable)
  const submitState: 'submitted' | 'ready' | 'locked' =
    stage !== 'Evaluated' ? 'submitted' : allPrepDone ? 'ready' : 'locked';

  const steps: JourneyStep[] = [
    { key: 'research',       label: zh ? '公司情报' : 'Intel',          state: intelState },
    { key: 'cover',          label: zh ? '求职信' : 'Cover letter',      state: coverState },
    { key: 'questions',      label: zh ? '面试问题' : 'Questions',       state: questionsState },
    { key: 'recommendation', label: zh ? '推荐信' : 'Recommendation',    state: recState },
    { key: 'submit',         label: zh ? '提交' : 'Submit',             state: submitState },
  ];

  // Pick the next-step CTA copy based on what's still missing.
  let cta: { text: string; sub: string };
  if (stage !== 'Evaluated') {
    cta = {
      text: zh ? `当前阶段：${stageLabel(stage, zh)}` : `Tracker: ${stage}`,
      sub: zh
        ? '阶段已经过了「已评估」。需要的话用页面顶部的下拉框修改阶段。'
        : 'Stage already past Evaluated. Use the header dropdown to change stage if needed.',
    };
  } else if (allPrepDone) {
    cta = {
      text: zh ? '可以提交了。' : 'Ready to submit.',
      sub: zh
        ? '4 项准备工作全部完成。去公司官网提交申请表，然后把这一条标记为「已申请」。'
        : 'All 4 prep tasks done. Submit the form on the company site, then mark this row as Applied.',
    };
  } else {
    const nextIdx = prepStates.findIndex((s) => s !== 'done');
    const nextStep = steps[nextIdx];
    cta = {
      text: zh ? `下一步：${nextStep.label}` : `Next: ${nextStep.label}`,
      sub: zh
        ? `4 项准备工作已完成 ${doneCount} 项。我们绝不替你提交——这些都是草稿，由你自己复制粘贴。`
        : `${doneCount} of 4 prep tasks done. We never submit on your behalf — these are drafts to copy/paste yourself.`,
    };
  }

  return (
    <div
      className="card"
      style={{
        padding: '18px 22px',
        background: stage !== 'Evaluated' ? 'rgba(0,0,0,0.02)' : 'rgba(45,122,74,0.04)',
        border: `1px solid ${stage !== 'Evaluated' ? 'var(--line)' : 'rgba(45,122,74,0.25)'}`,
      }}
    >
      <div className="tab" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>{zh ? '申请清单' : 'Apply checklist'}</span>
        <span style={{ color: 'var(--ink3)', textTransform: 'none', letterSpacing: 0 }}>
          {zh ? ` · 4 项准备已完成 ${doneCount} 项` : ` · ${doneCount} of 4 prep tasks done`}
        </span>
      </div>

      {/* The 5-node strip */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0, marginBottom: 14 }}>
        {steps.map((step, i) => {
          const isLast = i === steps.length - 1;
          const nextDone = !isLast && (steps[i + 1].state === 'done' || steps[i + 1].state === 'submitted');
          const thisDone = step.state === 'done' || step.state === 'submitted' || step.state === 'ready';
          // Connector colour: green if BOTH this step and the next are done/ready,
          // otherwise neutral. (We don't want a green line between done→idle.)
          const connectorActive = thisDone && nextDone;
          return (
            <div key={step.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0, position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {/* Left half-connector */}
                {i > 0 && (
                  <div style={{
                    flex: 1,
                    height: 2,
                    background: (steps[i - 1].state === 'done' || steps[i - 1].state === 'submitted' || steps[i - 1].state === 'ready') && thisDone
                      ? 'var(--good)'
                      : 'var(--line)',
                  }} />
                )}
                {i === 0 && <div style={{ flex: 1 }} />}
                <StepNode step={step} onClick={() =>
                  step.key === 'submit'
                    ? (submitState === 'ready' || submitState === 'locked') && onMarkApplied()
                    : onNavigate(step.key as StepKey)
                } />
                {/* Right half-connector */}
                {!isLast && (
                  <div style={{
                    flex: 1,
                    height: 2,
                    background: connectorActive ? 'var(--good)' : 'var(--line)',
                  }} />
                )}
                {isLast && <div style={{ flex: 1 }} />}
              </div>
              <div
                onClick={() =>
                  step.key === 'submit'
                    ? (submitState === 'ready' || submitState === 'locked') && onMarkApplied()
                    : onNavigate(step.key as StepKey)
                }
                style={{
                  fontSize: 11.5,
                  color: thisDone ? 'var(--ink)' : step.state === 'running' ? 'var(--pri)' : 'var(--ink3)',
                  marginTop: 8,
                  fontWeight: thisDone || step.state === 'running' ? 500 : 400,
                  textAlign: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%',
                }}
                title={step.label + ' · ' + step.state}
              >
                {step.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{cta.text}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginTop: 3, lineHeight: 1.5 }}>{cta.sub}</div>
        </div>
        {stage === 'Evaluated' && (
          <button
            className="btn accent"
            onClick={onMarkApplied}
            disabled={markingApplied}
            title={allPrepDone
              ? (zh ? '把状态改为「已申请」' : 'Flip tracker status to Applied')
              : (zh ? '你随时可以提交——准备工作是可选的' : 'You can submit any time — prep tasks are optional')}
          >
            {markingApplied ? (zh ? '更新中…' : 'Updating…') : (zh ? '标记为已申请' : 'Mark as Applied')}
          </button>
        )}
      </div>
    </div>
  );
}

// Single circular node in the journey strip — drives its own colour/icon
// from the `state` prop. 28px footprint to fit comfortably in the row.
function StepNode({ step, onClick }: { step: JourneyStep; onClick: () => void }) {
  const { state } = step;

  let bg = 'var(--surface)';
  let border = 'var(--line)';
  let color = 'var(--ink3)';
  let content: React.ReactNode = '○';

  if (state === 'done' || state === 'submitted') {
    bg = 'var(--good)';
    border = 'var(--good)';
    color = '#fff';
    content = '✓';
  } else if (state === 'running') {
    bg = 'var(--pri-soft)';
    border = 'var(--pri)';
    color = 'var(--pri)';
    content = '⟳';
  } else if (state === 'failed') {
    bg = 'rgba(180,58,58,0.1)';
    border = '#b43a3a';
    color = '#b43a3a';
    content = '!';
  } else if (state === 'ready') {
    // Submit ready — pulsy primary
    bg = 'var(--pri)';
    border = 'var(--pri)';
    color = '#fff';
    content = '▶';
  } else if (state === 'locked') {
    // Submit locked (prep incomplete) — soft outline
    bg = 'var(--surface)';
    border = 'var(--line)';
    color = 'var(--ink3)';
    content = '▷';
  }

  return (
    <div
      onClick={onClick}
      style={{
        width: 30,
        height: 30,
        borderRadius: '50%',
        background: bg,
        border: `1.5px solid ${border}`,
        color,
        display: 'grid',
        placeItems: 'center',
        fontSize: state === 'running' ? 14 : 12,
        fontWeight: 600,
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'all 120ms',
        animation: state === 'running' ? 'spin 1.6s linear infinite' : undefined,
      }}
    >
      {content}
    </div>
  );
}

// ── Shared card chrome ───────────────────────────────────────────────────────

interface ActionCardProps {
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
  badge?: React.ReactNode;        // "saved 2h ago", "running…", etc
  primaryAction?: React.ReactNode; // top-right button(s)
  children?: React.ReactNode;     // body shown below header
}
function ActionCard({ icon, title, description, badge, primaryAction, children }: ActionCardProps) {
  return (
    <div className="card" style={{ padding: 0 }}>
      <div
        style={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
          borderBottom: children ? '1px solid var(--line2)' : 'none',
        }}
      >
        <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--surface)', display: 'grid', placeItems: 'center', flexShrink: 0, color: 'var(--ink2)' }}>
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
            {badge && <div style={{ fontSize: 11, color: 'var(--ink3)' }}>{badge}</div>}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink3)', marginTop: 3, lineHeight: 1.5 }}>
            {description}
          </div>
        </div>
        {primaryAction && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            {primaryAction}
          </div>
        )}
      </div>
      {children && <div style={{ padding: '16px 20px' }}>{children}</div>}
    </div>
  );
}

// Helpers used by every card body.
function copyToClipboard(text: string) {
  navigator.clipboard?.writeText(text).catch(() => { /* noop */ });
}
async function downloadDocx(markdown: string, filename: string) {
  const res = await fetch('/api/render-docx', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ markdown, filename }),
  });
  if (!res.ok) {
    alert('DOCX render failed: ' + res.statusText);
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Deep research card ──────────────────────────────────────────────────────
function DeepResearchCard({ appId: _appId, company, role }: { appId: string; company: string; role: string }) {
  const zh = useZh();
  const slug = companySlugify(company);
  const companyLower = (company || '').toLowerCase();
  const runner = useJobRunner(`research-${slug}`, {
    reattach: { cmd: 'deep_research', match: (p) => String(p.company || '').toLowerCase() === companyLower },
  });

  const listQ = useQuery({
    queryKey: ['interview-prep-list', slug],
    queryFn: () =>
      apiGet<PrepListResponse>('/api/interview-prep/list', { company_slug: slug }),
    enabled: !!slug,
    staleTime: 5_000,
    refetchInterval: runner.isRunning ? 4000 : false,
  });
  const latestResearch = useMemo(() => {
    const items = (listQ.data?.items ?? []).filter((i) => i.kind === 'research');
    if (items.length === 0) return null;
    return items.sort((a, b) => b.mtime - a.mtime)[0];
  }, [listQ.data]);

  // Once the runner finishes, prefer rendering the saved file (clean) over
  // the live stdout (noisy). Falls back to cleanLLMOutput(runner.log) only
  // if the saved file couldn't be loaded.
  const savedQ = useQuery({
    queryKey: ['interview-prep-md', latestResearch?.file],
    queryFn: () =>
      fetch(latestResearch!.url).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      }),
    enabled: !!latestResearch?.url,
    staleTime: 30_000,
  });

  const runResearch = () => {
    runner.startJob('deep_research', { company, role });
  };

  // Determine what to show: progress (running) → saved file (preferred) →
  // cleaned runner log (fallback).
  const showRunningProgress = runner.isRunning;
  const showSaved = !runner.isRunning && !!savedQ.data;
  const showFallbackLog = !runner.isRunning && !savedQ.data && !!runner.log;

  const badge = runner.isRunning
    ? (zh ? `运行中 · ${runner.log.length.toLocaleString()} 字符` : `running · ${runner.log.length.toLocaleString()} chars`)
    : latestResearch
      ? (zh ? `已保存 ${fmtAge(latestResearch.mtime)} · ${(latestResearch.bytes / 1024).toFixed(1)} KB` : `saved ${fmtAge(latestResearch.mtime)} · ${(latestResearch.bytes / 1024).toFixed(1)} KB`)
      : (zh ? '约 2 分钟 · 本地运行' : '~2 min · runs locally');

  return (
    <ActionCard
      icon={<Telescope size={16} />}
      title={zh ? '公司情报' : 'Company intel'}
      description={zh
        ? 'AI 把公开信息整理成一份公司档案——业务战略、招聘情况、近期新闻、面试时可用的话题。会保存下来供你随时回看。'
        : 'LLM compiles public signals into a dossier — AI/ML strategy, hiring landscape, recent news, talking points to use in interviews. Saved so you can revisit.'}
      badge={badge}
      primaryAction={
        runner.isRunning ? (
          <button className="btn" onClick={runner.cancel} style={{ color: '#b43a3a', fontSize: 12 }}>{zh ? '取消' : 'Cancel'}</button>
        ) : (
          <button className="btn accent" onClick={runResearch} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Sparkles size={13} /> {latestResearch ? (zh ? '再跑一次' : 'Run again') : (zh ? '开始调研' : 'Research')}
          </button>
        )
      }
    >
      {showRunningProgress && (
        <div>
          <Progress value={Math.min(95, runner.log.length / 80)} />
          <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 6 }}>
            {zh
              ? `正在生成公司情报…（约 2 分钟）· 已输出 ${runner.log.length.toLocaleString()} 字符`
              : `Generating company intel… (~2 min) · ${runner.log.length.toLocaleString()} chars streamed`}
          </div>
          {runner.errorLine && (
            <div style={{ marginTop: 10, padding: '10px 12px', background: '#fcf3e6', border: '1px solid #eddbc1', borderRadius: 8, fontSize: 12, color: 'var(--warn)' }}>
              {runner.errorLine}
            </div>
          )}
        </div>
      )}

      {showSaved && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <button className="btn ghost" onClick={() => copyToClipboard(savedQ.data!)} style={{ fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Copy size={12} /> {zh ? '复制' : 'Copy'}
            </button>
          </div>
          <div className="report-md" style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--ink)', maxHeight: 600, overflowY: 'auto', padding: '12px 14px', background: 'rgba(0,0,0,0.02)', border: '1px solid var(--line2)', borderRadius: 6 }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{savedQ.data!}</ReactMarkdown>
          </div>
        </div>
      )}

      {showFallbackLog && (
        <div>
          <div style={{ fontSize: 11, color: 'var(--ink3)', marginBottom: 8 }}>
            {zh ? '保存的文件不可用——显示清理后的实时输出。' : 'Saved file not available — showing cleaned live output.'}
          </div>
          <div className="report-md" style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--ink)', maxHeight: 600, overflowY: 'auto', padding: '12px 14px', background: 'rgba(0,0,0,0.02)', border: '1px solid var(--line2)', borderRadius: 6 }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanLLMOutput(runner.log)}</ReactMarkdown>
          </div>
        </div>
      )}

      {!showRunningProgress && !showSaved && !showFallbackLog && (
        <div style={{ fontSize: 12, color: 'var(--ink3)', fontStyle: 'italic' }}>
          {savedQ.isLoading
            ? (zh ? '加载已保存的调研…' : 'Loading saved research…')
            : (zh ? '还没有调研。点「开始调研」开始。' : 'No research yet. Click Research to start.')}
        </div>
      )}
    </ActionCard>
  );
}

// ── Cover letter card ───────────────────────────────────────────────────────
function CoverLetterCard({ appId, company, role }: { appId: string; company: string; role: string }) {
  const zh = useZh();
  const companyLower = (company || '').toLowerCase();
  const roleLower = (role || '').toLowerCase();
  const runner = useJobRunner(`cover-letter-${appId}`, {
    reattach: {
      cmd: 'cover_letter',
      match: (p) =>
        String(p.company || '').toLowerCase() === companyLower &&
        String(p.role || '').toLowerCase() === roleLower,
    },
  });
  // cleanLetterOutput strips ```markdown … ``` fences and chat-style
  // preamble/postamble that qwen3:32b sometimes emits despite the prompt
  // saying "no meta commentary". Without this, those phrases ended up in the
  // .docx download as literal text.
  const cleanLog = useMemo(() => cleanLetterOutput(runner.log || ''), [runner.log]);
  // The backend now renders a matching PDF and emits >>COVER_PDF:{pdf} at the
  // end. Parse it so the draft is downloadable as a PDF, not just .docx.
  const coverPdf = useMemo(() => {
    const m = (runner.log || '').match(/>>COVER_PDF:(\{.*\})/);
    if (!m) return null;
    try { return JSON.parse(m[1]).pdf as string; } catch { return null; }
  }, [runner.log]);

  const generate = () => {
    runner.startJob('cover_letter', { company, role });
  };

  const filename = `cover-${companySlugify(company)}-${new Date().toISOString().slice(0, 10)}.docx`;

  const badge = runner.isRunning
    ? (zh ? `生成中 · ${runner.log.length.toLocaleString()} 字符` : `drafting · ${runner.log.length.toLocaleString()} chars`)
    : runner.status === 'done'
      ? (zh ? '草稿已就绪 · 发送前请自己过一遍' : 'draft ready · review before sending')
      : (zh ? '3 段 · 基于你的简历' : '3 paragraphs · grounded in your CV');

  return (
    <ActionCard
      icon={<FileText size={16} />}
      title={zh ? '生成求职信' : 'Draft a cover letter'}
      description={zh
        ? '自信、平实的开头 + 从你简历里挑出的 2 个具体例证 + 明确的结尾。不写套话。'
        : 'Confident plain-English open + 2 specific proofs from your CV + concrete close. No corporate filler.'}
      badge={badge}
      primaryAction={
        runner.isRunning ? (
          <button className="btn" onClick={runner.cancel} style={{ color: '#b43a3a', fontSize: 12 }}>{zh ? '取消' : 'Cancel'}</button>
        ) : (
          <button className="btn accent" onClick={generate} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Sparkles size={13} /> {runner.status === 'done' ? (zh ? '重新生成' : 'Re-generate') : (zh ? '生成' : 'Generate')}
          </button>
        )
      }
    >
      {runner.isRunning && (
        <Progress value={Math.min(95, runner.log.length / 40)} />
      )}
      {runner.errorLine && (
        <div style={{ padding: '10px 12px', background: '#fcf3e6', border: '1px solid #eddbc1', borderRadius: 8, fontSize: 12, color: 'var(--warn)', marginBottom: 10 }}>
          {runner.errorLine}
        </div>
      )}
      {!runner.isRunning && cleanLog && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <button className="btn ghost" onClick={() => copyToClipboard(cleanLog)} style={{ fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Copy size={12} /> {zh ? '复制' : 'Copy'}
            </button>
            <button className="btn" onClick={() => downloadDocx(cleanLog, filename)} style={{ fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Download size={12} /> {zh ? '下载 .docx' : 'Download .docx'}
            </button>
            {coverPdf && (
              <a className="btn" href={`/api/report?file=${encodeURIComponent('reports/' + coverPdf)}`}
                 style={{ fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Download size={12} /> {zh ? '下载 PDF' : 'Download PDF'}
              </a>
            )}
            <button className="btn ghost" onClick={runner.dismiss} style={{ fontSize: 11.5, color: 'var(--ink3)' }}>{zh ? '清除' : 'Clear'}</button>
          </div>
          <div className="report-md" style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--ink)', padding: '12px 14px', background: 'rgba(0,0,0,0.02)', border: '1px solid var(--line2)', borderRadius: 6 }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanLog}</ReactMarkdown>
          </div>
        </div>
      )}
      {runner.isRunning && cleanLog === '' && (
        <div style={{ fontSize: 12, color: 'var(--ink3)', fontStyle: 'italic', marginTop: 6 }}>
          {zh ? '正在启动 Ollama…' : 'Warming up Ollama…'}
        </div>
      )}
    </ActionCard>
  );
}

// ── Application questions card ──────────────────────────────────────────────
// The only card that needs an input step. Click "Open" to expand the input
// area (textarea + PDF upload), then Generate to draft answers.
interface ParsedAnswer { heading: string; body: string }

// Trim a duplicate `**Question:** ... **Answer:** ...` block that some Ollama
// outputs append after the canonical `## Q<n>:` answer. Without this trim we
// end up rendering the same Q&A twice — once as the headline answer, again as
// a fake separate card sourced from Strategy 2 below.
function stripDuplicateQABlock(body: string): string {
  return body
    .replace(/\n\s*---+\s*\n[\s\S]*$/, '')                            // anything after a horizontal rule
    .replace(/\n\s*\*\*(?:Question|Answer)\*?\*?[:.][\s\S]*$/i, '')   // the explicit **Question:** / **Answer:** block
    .trim();
}

function parseAnswersStrict(raw: string): ParsedAnswer[] {
  if (!raw) return [];
  const text = raw.replace(/\r\n/g, '\n').trim();

  // Strategy 1 (preferred): `## Q1: …` / `## Q1` / `### Q1` — match what the
  // prompt requires the LLM to emit. If we find at least one such heading,
  // we lock in to this strategy and never fall through (avoids the "two
  // cards for the same Q" failure mode where a stray **Question:** later in
  // the text would also match Strategy 2).
  const byHashHeading = text.split(/\n(?=#{2,4}\s*Q\s*\d+\b)/g);
  const firstStartsWithHeading = /^#{2,4}\s*Q\s*\d+\b/.test(byHashHeading[0]);
  if (byHashHeading.length > 1 || firstStartsWithHeading) {
    const out = byHashHeading
      .map((seg) => {
        const lines = seg.split('\n');
        const first = lines.shift() || '';
        return {
          heading: first.replace(/^#{2,4}\s*/, '').trim(),
          body: stripDuplicateQABlock(lines.join('\n').trim()),
        };
      })
      .filter((p) => p.heading);
    return out;
  }

  // Strategy 2: `**Question 1:**` / `**Q1:**` (only when no `## Q<n>:` exists)
  const byBoldHeading = text.split(/\n(?=\*\*(?:Question|Q)\s*\d*[:.]?)/g);
  if (byBoldHeading.length > 1) {
    return byBoldHeading.map((seg) => {
      const i = seg.indexOf('\n');
      const first = (i > 0 ? seg.slice(0, i) : seg).trim();
      const rest = i > 0 ? seg.slice(i + 1).trim() : '';
      return {
        heading: first.replace(/^\*\*|\*\*$/g, '').trim(),
        body: stripDuplicateQABlock(rest),
      };
    });
  }

  // Strategy 3: `Q1:` or `1.` line prefix
  const byMarker = text.split(/\n(?=(?:Q\s*\d+[:.)]|\d+[.)])\s)/g);
  if (byMarker.length > 1) {
    return byMarker.map((seg) => {
      const i = seg.indexOf('\n');
      const first = (i > 0 ? seg.slice(0, i) : seg).trim();
      const rest = i > 0 ? seg.slice(i + 1).trim() : '';
      return { heading: first, body: stripDuplicateQABlock(rest) };
    });
  }

  // No structure detected — return the whole thing as one block. We do NOT
  // fall back to splitting on blank lines (that's how noise lines became
  // fake "Answer 1 / 2 / 3" cards in the previous version).
  return [{ heading: 'Draft', body: stripDuplicateQABlock(text) }];
}

function QuestionsCard({ appId, company, role }: { appId: string; company: string; role: string }) {
  const zh = useZh();
  const companyLower = (company || '').toLowerCase();
  const roleLower = (role || '').toLowerCase();
  const runner = useJobRunner(`apply-answers-${appId}`, {
    reattach: {
      cmd: 'apply_assist',
      match: (p) =>
        String(p.company || '').toLowerCase() === companyLower &&
        String(p.role || '').toLowerCase() === roleLower,
    },
  });
  const liveLog = useMemo(() => cleanLLMOutput(runner.log || ''), [runner.log]);
  const liveAnswers = useMemo(() => parseAnswersStrict(liveLog), [liveLog]);

  // Persisted history (cross-session) + jobId-locked dedup set.
  const { batches, addBatch, removeBatch, clearAll, isConsumed } = useQuestionBatches(appId);

  // In-memory mirror of consumed jobIds for the current session, so we don't
  // hit localStorage on every effect tick. Persistent set is the authoritative
  // source; this is just a fast path.
  const consumedJobIdsRef = useRef<Set<string>>(new Set());

  // Capture the input that *triggered* the current run, so we can persist it
  // alongside the answers when the job finishes (the textarea may have been
  // edited or cleared between Generate and Done).
  const submittedRef = useRef<string>('');
  const [formText, setFormText] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // jobId-locked done-watch: only depends on `runner.status` + `runner.jobId`.
  // Streaming-time updates to runner.log do NOT re-trigger this effect, which
  // is what we want — the effect runs once per terminal transition, reads the
  // log fresh inside, then marks the jobId consumed (in-memory + persisted).
  useEffect(() => {
    if (runner.status !== 'done') return;
    if (!runner.jobId) return;
    if (consumedJobIdsRef.current.has(runner.jobId)) return;
    if (isConsumed(runner.jobId)) {
      // Persisted dedup: this jobId was already turned into a batch in a
      // previous session. Mark in-memory too so we don't keep checking.
      consumedJobIdsRef.current.add(runner.jobId);
      return;
    }

    const log = cleanLLMOutput(runner.log || '');
    const parsed = parseAnswersStrict(log);
    if (parsed.length === 0) return;

    consumedJobIdsRef.current.add(runner.jobId);
    addBatch({
      jobId: runner.jobId,
      ts: Date.now(),
      questions: submittedRef.current,
      answers: parsed,
    });
    submittedRef.current = '';
    setFormText('');
    // We deliberately do NOT call runner.dismiss(). Letting the runner stay
    // in 'done' state with its stale log is harmless because:
    //   - The next startJob() flips jobId to a new value (new SSE, fallbackLog
    //     reset by useJobRunner.ts), so live UI shows fresh data.
    //   - This effect won't re-add for the same jobId because of consumedSet.
    //   - The persisted consumed set survives reloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runner.status, runner.jobId, addBatch, isConsumed]);

  const onPdf = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    setPdfBusy(true);
    setPdfError(null);
    try {
      const res = await fetch('/api/pdf-extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/pdf' },
        body: file,
      });
      const j = await res.json();
      if (!res.ok || !j.success) throw new Error(j.error || 'extract failed');
      setFormText((cur) => (cur ? cur.trim() + '\n\n' : '') + (j.text || '').trim());
    } catch (e) {
      setPdfError((e as Error).message);
    } finally {
      setPdfBusy(false);
    }
  };

  const generate = () => {
    if (!formText.trim() || runner.isRunning) return;
    submittedRef.current = formText.trim();
    runner.startJob('apply_assist', { company, role, questions: formText.trim() });
  };

  const clearAllBatches = () => {
    if (batches.length === 0) return;
    if (!window.confirm(zh
      ? `删除全部 ${batches.length} 批已保存的回答？这些草稿只存在本地，不在服务器上。`
      : `Delete all ${batches.length} batch${batches.length === 1 ? '' : 'es'} of saved answers? This is local only — drafts are not on the server.`)) return;
    clearAll();
  };

  const totalAnswers = batches.reduce((sum, b) => sum + b.answers.length, 0);
  const badge = runner.isRunning
    ? (zh ? `生成中 · ${runner.log.length.toLocaleString()} 字符` : `drafting · ${runner.log.length.toLocaleString()} chars`)
    : batches.length > 0
      ? (zh ? `${batches.length} 批共 ${totalAnswers} 个回答` : `${totalAnswers} answer${totalAnswers === 1 ? '' : 's'} across ${batches.length} batch${batches.length === 1 ? '' : 'es'}`)
      : (zh ? '粘贴问题或上传 PDF' : 'paste questions or upload PDF');

  return (
    <ActionCard
      icon={<MessageSquare size={16} />}
      title={zh ? '回答申请表问题' : 'Answer the application form'}
      description={zh
        ? '把申请表里的问题粘进来（一行一个），或上传申请表 PDF。每次生成会存成独立的一批——旧的批次保留在下面，方便你之后回来处理同一职位的追加问题。'
        : "Paste the form's questions (one per line) or upload the application PDF. Each Generate becomes its own saved batch — older batches stay below so you can come back later for the same role's follow-up questions."}
      badge={badge}
    >
      <div>
        {/* ── Input ─────────────────────────────────────────────────── */}
        <textarea
          value={formText}
          onChange={(e) => setFormText(e.target.value)}
          rows={6}
          placeholder={zh
            ? `为什么选择 ${company}？\n讲一次你在压力下完成任务的经历。\n你为什么适合这个岗位？`
            : `Why ${company}?\nDescribe a time you shipped under pressure.\nWhat would you change about the team's roadmap?`}
          style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, fontFamily: 'Inter, var(--cjk-sans), sans-serif', background: 'var(--bg)', resize: 'vertical', lineHeight: 1.55 }}
        />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
          <input ref={fileRef} type="file" accept="application/pdf" onChange={onPdf} style={{ display: 'none' }} />
          <button className="btn" onClick={() => fileRef.current?.click()} disabled={pdfBusy} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
            <Upload size={12} /> {pdfBusy ? (zh ? '提取中…' : 'Extracting…') : (zh ? '上传 PDF' : 'Upload PDF')}
          </button>
          {pdfError && <span style={{ fontSize: 11.5, color: 'var(--warn)' }}>{pdfError}</span>}
          <div style={{ flex: 1 }} />
          {runner.isRunning ? (
            <button className="btn" onClick={runner.cancel} style={{ color: '#b43a3a', fontSize: 12 }}>{zh ? '取消' : 'Cancel'}</button>
          ) : (
            <button className="btn accent" onClick={generate} disabled={!formText.trim()} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={13} /> {zh ? '生成' : 'Generate'}
            </button>
          )}
        </div>

        {/* ── In-flight progress + live preview ─────────────────────── */}
        {/* key={runner.jobId} forces a clean unmount/remount when the user
            kicks off a new job, so we never render the previous job's stats
            in the new job's frame. */}
        {runner.isRunning && (
          <div key={runner.jobId} style={{ marginTop: 12 }}>
            <Progress value={Math.min(95, runner.log.length / 50)} />
            <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 6 }}>
              {zh
                ? `已输出 ${runner.log.length.toLocaleString()} 字符 · 完成后回答会出现在下方历史里`
                : `${runner.log.length.toLocaleString()} chars streamed · answers will land in history when done`}
            </div>
            {liveAnswers.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--ink3)' }}>
                {zh ? `已生成：${liveAnswers.length}` : `Drafted so far: ${liveAnswers.length}`}
              </div>
            )}
          </div>
        )}
        {runner.errorLine && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: '#fcf3e6', border: '1px solid #eddbc1', borderRadius: 8, fontSize: 12, color: 'var(--warn)' }}>
            {runner.errorLine}
          </div>
        )}

        {/* ── History ───────────────────────────────────────────────── */}
        {batches.length > 0 && (
          <div style={{ marginTop: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div className="tab" style={{ flex: 1 }}>
                {zh ? `历史 · ${batches.length} 批 · 保存在本地` : `History · ${batches.length} batch${batches.length === 1 ? '' : 'es'} · saved locally`}
              </div>
              <button
                className="btn ghost"
                onClick={clearAllBatches}
                style={{ fontSize: 11, color: '#b43a3a' }}
                title={zh ? '删除这条申请下所有已保存的批次（仅本地）' : 'Delete every saved batch for this application (local only)'}
              >
                {zh ? '全部清除' : 'Clear all'}
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {batches.map((batch) => (
                <QuestionBatchCard
                  key={batch.id}
                  batch={batch}
                  onDelete={() => removeBatch(batch.id)}
                  onReuseInput={() => setFormText(batch.questions)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </ActionCard>
  );
}

// One batch = one Generate run = one row of questions + their answers.
// Header is collapsible; per-card Copy keeps the existing UX.
function QuestionBatchCard({
  batch,
  onDelete,
  onReuseInput,
}: {
  batch: QuestionBatch;
  onDelete: () => void;
  onReuseInput: () => void;
}) {
  const zh = useZh();
  const [armedDelete, setArmedDelete] = useState(false);
  useEffect(() => {
    if (!armedDelete) return;
    const t = setTimeout(() => setArmedDelete(false), 3000);
    return () => clearTimeout(t);
  }, [armedDelete]);

  const handleDelete = () => {
    if (!armedDelete) { setArmedDelete(true); return; }
    setArmedDelete(false);
    onDelete();
  };

  const tsLabel = new Date(batch.ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <div style={{ border: '1px solid var(--line2)', borderRadius: 8, overflow: 'hidden' }}>
      <div
        style={{
          padding: '10px 14px',
          background: 'var(--surface)',
          borderBottom: '1px solid var(--line2)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 11.5,
          color: 'var(--ink3)',
        }}
      >
        <span style={{ fontWeight: 500, color: 'var(--ink2)' }}>{tsLabel}</span>
        <span>{zh ? `· ${batch.answers.length} 个回答` : `· ${batch.answers.length} answer${batch.answers.length === 1 ? '' : 's'}`}</span>
        <div style={{ flex: 1 }} />
        <button
          className="btn ghost"
          onClick={onReuseInput}
          style={{ fontSize: 10.5, padding: '2px 8px' }}
          title={zh ? '把这批问题填回输入框，方便你改了再跑一次' : "Copy this batch's questions back to the textarea so you can re-run with edits"}
        >
          {zh ? '重用问题' : 'Reuse input'}
        </button>
        <button
          className="btn ghost"
          onClick={handleDelete}
          style={{
            fontSize: 10.5,
            padding: '2px 8px',
            color: armedDelete ? '#fff' : '#b43a3a',
            background: armedDelete ? '#b43a3a' : 'transparent',
            border: armedDelete ? '1px solid #b43a3a' : undefined,
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}
          title={armedDelete ? (zh ? '再点一次删除这一批' : 'Click again to delete this batch') : (zh ? '删除这一批' : 'Delete this batch')}
        >
          {armedDelete ? (zh ? '确认？' : 'Sure?') : <><Trash2 size={11} /></>}
        </button>
      </div>
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {batch.answers.map((a, i) => (
          <div key={i} style={{ padding: '10px 12px', background: 'rgba(0,0,0,0.02)', border: '1px solid var(--line2)', borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
              <div style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                {a.heading || (zh ? `回答 ${i + 1}` : `Answer ${i + 1}`)}
              </div>
              <button className="btn ghost" onClick={() => copyToClipboard(a.body || a.heading)} style={{ fontSize: 11, padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Copy size={11} /> Copy
              </button>
            </div>
            <div className="report-md" style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--ink)' }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{a.body || '—'}</ReactMarkdown>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Recommendation letter card ──────────────────────────────────────────────
function RecommendationCard({ appId, company, role }: { appId: string; company: string; role: string }) {
  const zh = useZh();
  const companyLower = (company || '').toLowerCase();
  const roleLower = (role || '').toLowerCase();
  const runner = useJobRunner(`recommendation-${appId}`, {
    reattach: {
      cmd: 'tailor_recommendation',
      match: (p) =>
        String(p.company || '').toLowerCase() === companyLower &&
        String(p.role || '').toLowerCase() === roleLower,
    },
  });
  // Same letter-specific cleanup as Cover letter — strip markdown code fences,
  // LLM preamble/postamble, "**Adapted Recommendation Letter**" labels.
  const cleanLog = useMemo(() => cleanLetterOutput(runner.log || ''), [runner.log]);
  const qc = useQueryClient();

  const [sourceSlug, setSourceSlug] = useState<string>('');
  const [context, setContext] = useState<string>('');

  // Library of saved recommendation letters
  const listQ = useQuery({
    queryKey: ['recommendations-list'],
    queryFn: () => apiGet<{ items: RecommendationItem[] }>('/api/recommendations/list'),
    staleTime: 30_000,
  });
  const items = listQ.data?.items ?? [];

  // Default-pick the first letter once the list loads if user hasn't chosen.
  useEffect(() => {
    if (!sourceSlug && items.length > 0) setSourceSlug(items[0].slug);
  }, [items, sourceSlug]);

  const generate = () => {
    if (!sourceSlug || runner.isRunning) return;
    runner.startJob('tailor_recommendation', {
      company,
      role,
      source_slug: sourceSlug,
      context: context.trim() || undefined,
    });
  };

  const filename = `recommendation-${companySlugify(company)}-${new Date().toISOString().slice(0, 10)}.docx`;

  const badge = runner.isRunning
    ? (zh ? `改写中 · ${runner.log.length.toLocaleString()} 字符` : `adapting · ${runner.log.length.toLocaleString()} chars`)
    : runner.status === 'done'
      ? (zh ? '草稿已就绪 · 请逐句核对' : 'draft ready · review every claim')
      : items.length === 0
        ? (zh ? '先上传一封你已有的推荐信' : 'upload your existing letter to start')
        : (zh ? `已存 ${items.length} 封推荐信` : `${items.length} letter${items.length === 1 ? '' : 's'} on file`);

  return (
    <ActionCard
      icon={<Handshake size={16} />}
      title={zh ? '定制推荐信' : 'Tailor a recommendation letter'}
      description={zh
        ? '把你已有的推荐信（老板／教授／同事写的）调整得更贴合这个职位。保留推荐人的语气和原有事例，只重新调整侧重点。'
        : "Adapts an existing letter (boss / professor / peer) to fit this JD. Preserves the recommender's voice and anecdotes; only re-frames the emphasis."}
      badge={badge}
    >
      {true && (
        <div>
          <RecommendationLibrary
            items={items}
            value={sourceSlug}
            onChange={setSourceSlug}
            onChanged={() => qc.invalidateQueries({ queryKey: ['recommendations-list'] })}
          />

          <div style={{ marginTop: 12 }}>
            <div className="tab" style={{ marginBottom: 4 }}>{zh ? '补充说明（选填）' : 'Optional context'}</div>
            <input
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder={zh ? '例如：他特别提到我的执行速度——请重点突出这一点' : 'e.g. He specifically mentioned my execution speed — emphasise that'}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, fontFamily: 'Inter, var(--cjk-sans), sans-serif', background: 'var(--bg)' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
            <div style={{ flex: 1 }} />
            {runner.isRunning ? (
              <button className="btn" onClick={runner.cancel} style={{ color: '#b43a3a', fontSize: 12 }}>{zh ? '取消' : 'Cancel'}</button>
            ) : (
              <button
                className="btn accent"
                onClick={generate}
                disabled={!sourceSlug}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                title={!sourceSlug ? (zh ? '请先选择或上传一封原始推荐信' : 'Pick or upload a source letter first') : (zh ? '把这封信调整得贴合这个职位' : 'Adapt this letter to the JD')}
              >
                <Sparkles size={13} /> {runner.status === 'done' ? (zh ? '重新定制' : 'Re-tailor') : (zh ? '开始定制' : 'Tailor')}
              </button>
            )}
          </div>

          {runner.isRunning && (
            <div style={{ marginTop: 12 }}>
              <Progress value={Math.min(95, runner.log.length / 40)} />
              <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 6 }}>
                {runner.log.length.toLocaleString()} chars streamed
              </div>
            </div>
          )}
          {runner.errorLine && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#fcf3e6', border: '1px solid #eddbc1', borderRadius: 8, fontSize: 12, color: 'var(--warn)' }}>
              {runner.errorLine}
            </div>
          )}
          {!runner.isRunning && cleanLog && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <button className="btn ghost" onClick={() => copyToClipboard(cleanLog)} style={{ fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Copy size={12} /> {zh ? '复制' : 'Copy'}
                </button>
                <button className="btn" onClick={() => downloadDocx(cleanLog, filename)} style={{ fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Download size={12} /> {zh ? '下载 .docx' : 'Download .docx'}
                </button>
                <button className="btn ghost" onClick={runner.dismiss} style={{ fontSize: 11.5, color: 'var(--ink3)' }}>{zh ? '清除' : 'Clear'}</button>
              </div>
              <div className="report-md" style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--ink)', padding: '12px 14px', background: 'rgba(0,0,0,0.02)', border: '1px solid var(--line2)', borderRadius: 6 }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanLog}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      )}
    </ActionCard>
  );
}

// Sub-component for the rec letter card: dropdown of saved letters + Upload
// new (with display name prompt) + per-letter Delete.
function RecommendationLibrary({
  items,
  value,
  onChange,
  onChanged,
}: {
  items: RecommendationItem[];
  value: string;
  onChange: (slug: string) => void;
  onChanged: () => void;
}) {
  const zh = useZh();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onUpload = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    const name = window.prompt(
      zh ? '给这封推荐信起个名字（例如「导师推荐信 2024」）：' : 'Name this letter (e.g. "Boss recommendation 2024"):',
      file.name.replace(/\.\w+$/, ''));
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Display-Name': encodeURIComponent(name),
          'X-Filename': encodeURIComponent(file.name),
        },
        body: file,
      });
      const j = await res.json();
      if (!res.ok || !j.success) throw new Error(j.error || 'upload failed');
      onChanged();
      onChange(j.slug);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug: string) => {
    const ok = window.confirm(zh
      ? `删除推荐信「${slug}」？原文会被移除，已经生成的草稿会保留。`
      : `Delete recommendation letter "${slug}"? The source text will be removed from data/recommendations/. Drafts already generated stay.`);
    if (!ok) return;
    try {
      const res = await fetch(`/api/recommendations/${encodeURIComponent(slug)}`, { method: 'DELETE' });
      const j = await res.json();
      if (!res.ok || !j.ok) throw new Error(j.error || 'delete failed');
      onChanged();
      if (value === slug) onChange('');
    } catch (e) {
      alert((zh ? '删除失败：' : 'Delete failed: ') + (e as Error).message);
    }
  };

  return (
    <div>
      <div className="tab" style={{ marginBottom: 4 }}>{zh ? '原始推荐信' : 'Source letter'}</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={items.length === 0}
          style={{ flex: 1, minWidth: 200, padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)' }}
        >
          {items.length === 0 && <option value="">{zh ? '— 还没有上传推荐信 —' : '— no letters uploaded —'}</option>}
          {items.map((it) => (
            <option key={it.slug} value={it.slug}>
              {it.name} · {(it.bytes / 1024).toFixed(1)} KB
            </option>
          ))}
        </select>
        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" onChange={onUpload} style={{ display: 'none' }} />
        <button className="btn" onClick={() => fileRef.current?.click()} disabled={busy} style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Upload size={12} /> {busy ? (zh ? '上传中…' : 'Uploading…') : (zh ? '上传新的' : 'Upload new')}
        </button>
        {value && (
          <button
            className="btn ghost"
            onClick={() => remove(value)}
            style={{ fontSize: 12, color: '#b43a3a', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            title={zh ? '从库中删除这封推荐信' : 'Delete this letter from the library'}
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
      {error && <div style={{ fontSize: 11.5, color: 'var(--warn)', marginTop: 6 }}>{error}</div>}
    </div>
  );
}

function ReportViewer({ file }: { file: string }) {
  const q = useQuery({
    queryKey: ['report', file],
    queryFn: () =>
      fetch(`/api/report?file=reports/${encodeURIComponent(file)}`).then((r) => r.text()),
  });
  return (
    <div
      className="report-md"
      style={{
        fontSize: 13.5,
        lineHeight: 1.65,
        color: 'var(--ink)',
        maxHeight: 640,
        overflowY: 'auto',
      }}
    >
      {q.isLoading && 'Loading…'}
      {q.data ? (
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{q.data}</ReactMarkdown>
      ) : (
        !q.isLoading && <span style={{ color: 'var(--ink3)' }}>Empty or missing file.</span>
      )}
    </div>
  );
}
